/**
 * TimelineEventMarkerAtom - Visual marker for sphere timeline events
 * Displays colored circular marker with event-specific icon
 */

import React from 'react';
import { TimelineEventType, TimelineEventCategory } from 'types/timeline';
import './TimelineEventMarkerAtom.scss';

export interface TimelineEventMarkerAtomProps {
  eventType: TimelineEventType;
  category: TimelineEventCategory;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Maps event types to PrimeReact icon classes.
 *
 * Keyed on `LedgerEntryType` (EAINT-764 Stage 2 / OQ-3). The previous map keyed on the legacy
 * PascalCase vocabulary the API no longer sends, so every marker fell back to the default icon.
 */
const EVENT_ICON_MAP: Record<TimelineEventType, string> = {
  sphere_created: 'pi-plus-circle',
  sphere_property_changed: 'pi-pencil',
  permission_changed: 'pi-shield',
  arc_transition: 'pi-replay',
  arc_lifecycle: 'pi-sync',
  arc_answered: 'pi-comment',
  stage_action: 'pi-bolt',
  check_in: 'pi-check-square',
  task_completed: 'pi-check-circle',
  factor_lifecycle: 'pi-sliders-h',
  perspective_assigned: 'pi-user-plus',
  perspective_unassigned: 'pi-user-minus',
  aspect_observation: 'pi-eye',
  aspect_validation: 'pi-verified',
  aspect_attached: 'pi-link',
  aspect_detached: 'pi-times-circle',
  attachment_added: 'pi-paperclip',
  attachment_removed: 'pi-trash',
  status_propagated: 'pi-share-alt',
  velocity_changed: 'pi-forward',
  impact_changed: 'pi-chart-line',
};

const TimelineEventMarkerAtom: React.FC<TimelineEventMarkerAtomProps> = ({
  eventType,
  category,
  size = 'medium',
  className = '',
}) => {
  const icon = EVENT_ICON_MAP[eventType] || 'pi-circle';

  return (
    <div
      className={`timeline-event-marker-atom timeline-event-marker-atom--${size} timeline-event-marker-atom--${category} ${className}`}
      title={eventType}
    >
      <i className={`pi ${icon}`} />
    </div>
  );
};

export default TimelineEventMarkerAtom;
